import { toast } from "sonner";
import { Leaf, Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/context/LanguageContext";
import { LoadingState } from "@/components/LoadingState";
import { FlowerCard } from "@/components/flower/FlowerCard";

// Results Panel (right column): value cards, loading and empty states
export const ResultsPanel = ({ results, isLoading }) => {
  const { t } = useLanguage();

  const handleDownloadAll = async () => {
    toast.info(t.downloading);
    // Trigger download for each card with delay
    const cards = document.querySelectorAll('[data-testid^="download-card-"]');
    for (const card of cards) {
      card.click();
      await new Promise(r => setTimeout(r, 1500));
    }
  };

  return (
    <div className="lg:col-span-3 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg md:text-xl" style={{ color: '#1A3C34' }}>
          {t.valueCards}
        </h3>
        {results?.flores?.length > 1 && (
          <button
            onClick={handleDownloadAll}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
            style={{
              backgroundColor: '#1A3C34',
              color: '#F9F7F2'
            }}
            data-testid="download-all-button"
          >
            <Download className="w-4 h-4" />
            {t.downloadAll}
          </button>
        )}
      </div>

      {isLoading ? (
        <LoadingState />
      ) : results ? (
        <div className="space-y-4" data-testid="results-container">
          {results.descripcion_general && (
            <Card className="border-0 shadow-lg" style={{ backgroundColor: 'rgba(163, 177, 138, 0.1)' }}>
              <CardContent className="p-4 md:p-6">
                <p className="font-body text-sm" style={{ color: '#1A3C34' }}>
                  {results.descripcion_general}
                </p>
              </CardContent>
            </Card>
          )}

          {results.flores.length > 0 ? (
            <div className="space-y-4">
              {results.flores.map((flower, index) => (
                <FlowerCard key={`${flower.nombre_comun}-${flower.nombre_cientifico}`} flower={flower} index={index} />
              ))}
            </div>
          ) : (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Leaf className="w-12 h-12 mx-auto mb-4" style={{ color: '#6B705C' }} />
                <p className="font-body" style={{ color: '#6B705C' }}>
                  {t.noFlowersFound}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card className="border-0 shadow-lg" data-testid="empty-results">
          <CardContent className="p-8 md:p-12 text-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: 'rgba(163, 177, 138, 0.15)' }}
            >
              <Leaf className="w-10 h-10" style={{ color: '#A3B18A' }} />
            </div>
            <p className="font-heading text-lg mb-2" style={{ color: '#1A3C34' }}>
              {t.noResultsYet}
            </p>
            <p className="font-body text-sm mb-4" style={{ color: '#6B705C' }}>
              {t.noResultsDescription}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Badge variant="outline" className="text-xs" style={{ borderColor: '#DAD7CD' }}>{t.availableColors}</Badge>
              <Badge variant="outline" className="text-xs" style={{ borderColor: '#DAD7CD' }}>{t.vasLife}</Badge>
              <Badge variant="outline" className="text-xs" style={{ borderColor: '#DAD7CD' }}>{t.floraLifeProducts}</Badge>
              <Badge variant="outline" className="text-xs" style={{ borderColor: '#DAD7CD' }}>{t.care}</Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
